import { MAX_CACHED_TEXT, MAX_WINDOWS, NOTE_WINDOWS_VERSION } from './types';
import type { NoteWindow } from './types';

/**
 * Note windows survive a reload through localStorage, keyed per user.
 * See docs/MULTI_APP_WORKSPACE.md §7.
 *
 * Every read and write is defensive: private-mode browsers and "block site
 * data" settings throw on access, and corrupt storage must never white-screen
 * the app — a bad payload is dropped, silently.
 */

export const NOTE_WINDOWS_PREFIX = 'mm:noteWindows';

interface Snapshot {
  version: number;
  windows: NoteWindow[];
  maxZ: number;
}

export function noteWindowsKey(userId: number | null | undefined): string | null {
  return userId == null ? null : `${NOTE_WINDOWS_PREFIX}:v${NOTE_WINDOWS_VERSION}:${userId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value.slice(0, MAX_CACHED_TEXT) : null;
}

function parseWindow(raw: unknown): NoteWindow | null {
  if (!isRecord(raw)) return null;
  const noteId = typeof raw['noteId'] === 'number' ? raw['noteId'] : null;
  if (noteId == null || !Number.isFinite(noteId)) return null;

  const locked = raw['locked'] === true;
  const openedAt = typeof raw['openedAt'] === 'number' ? raw['openedAt'] : Date.now();

  return {
    noteId,
    title: asText(raw['title']),
    snippet: locked ? null : asText(raw['snippet']),
    color: asText(raw['color']),
    locked,
    state: raw['state'] === 'open' ? 'open' : 'minimized',
    mode: raw['mode'] === 'maximized' ? 'maximized' : 'floating',
    z: typeof raw['z'] === 'number' ? raw['z'] : 0,
    openedAt,
    lastFocusedAt: typeof raw['lastFocusedAt'] === 'number' ? raw['lastFocusedAt'] : openedAt,
  };
}

/**
 * Restores the dock. Windows come back **minimized** except the one that was
 * focused last — landing on a page under a stack of open windows would be
 * hostile. §5.1 rule 10.
 */
export function loadNoteWindows(userId: number | null | undefined): {
  windows: NoteWindow[];
  maxZ: number;
} {
  const key = noteWindowsKey(userId);
  const empty = { windows: [], maxZ: 0 };
  if (!key) return empty;

  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return empty;
  }
  if (!raw) return empty;

  let snapshot: unknown;
  try {
    snapshot = JSON.parse(raw);
  } catch {
    clearNoteWindows(userId);
    return empty;
  }

  if (!isRecord(snapshot) || snapshot['version'] !== NOTE_WINDOWS_VERSION) {
    clearNoteWindows(userId);
    return empty;
  }

  const list = Array.isArray(snapshot['windows']) ? snapshot['windows'] : [];
  const parsed = list
    .map(parseWindow)
    .filter((win): win is NoteWindow => win !== null)
    .sort((a, b) => b.lastFocusedAt - a.lastFocusedAt)
    .slice(0, MAX_WINDOWS);

  const mostRecent = parsed[0];
  const windows = parsed.map((win) => ({
    ...win,
    state: win === mostRecent && win.state === 'open' ? ('open' as const) : ('minimized' as const),
  }));

  const maxZ = windows.reduce((acc, win) => Math.max(acc, win.z), 0);
  return { windows, maxZ };
}

export function saveNoteWindows(
  userId: number | null | undefined,
  windows: NoteWindow[],
  maxZ: number,
): void {
  const key = noteWindowsKey(userId);
  if (!key) return;

  const snapshot: Snapshot = {
    version: NOTE_WINDOWS_VERSION,
    maxZ,
    windows: windows.map((win) => ({
      ...win,
      title: asText(win.title),
      // Never persist content for a locked note.
      snippet: win.locked ? null : asText(win.snippet),
    })),
  };

  try {
    if (windows.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(snapshot));
  } catch {
    // Quota or blocked storage — the in-memory state stays correct.
  }
}

export function clearNoteWindows(userId: number | null | undefined): void {
  const key = noteWindowsKey(userId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Drops every `mm:`-namespaced key — used on logout, for any user. */
export function clearAllWorkspaceStorage(): void {
  try {
    const doomed = Object.keys(localStorage).filter((key) => key.startsWith('mm:'));
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}
